/**
 * Email Campaign Constants
 * Shared between frontend and backend
 *
 * SOURCE OF TRUTH: This is the main file for email campaign constants.
 * Both the frontend and Cloud Functions import from this file.
 */

// Campaign Types
export const CAMPAIGN_TYPES = {
  MARKETING: 'marketing',
  FEATURE_ANNOUNCEMENT: 'feature_announcement'
}

// Filter Options
export const FILTER_OPTIONS = {
  ALL_USERS: 'All Users',
  MARKETING_OPT_IN: 'Marketing Opt-in Only (All Emails)',
  ALL_MARKETING_OPT_IN: 'All MARKETING (All Emails + Features Only)',
  FEATURES_ONLY: 'Features Only (Exclude Marketing)',
  ALL_MARKETING_OPT_OUTS: 'All Marketing Opt-outs',
  EXCLUDE_BY_DATE: 'Exclude Users By Date',
  EXCLUDE_BY_DATE_RANGE: 'Exclude Users By Date Range',
  EXCLUDE_TODAYS_RECIPIENTS: "Exclude Today's Email Recipients",
  SELECT_NOT_EMAILED_TODAY: 'Select Users Not Emailed Today',
  RECENT_USERS_30_DAYS: 'Recent Users (30 days)',
  INACTIVE_USERS_90_DAYS: 'Inactive Users (90+ days)'
}

// Map filters to campaign types
export const FILTER_TO_CAMPAIGN_TYPE = {
  [FILTER_OPTIONS.ALL_MARKETING_OPT_IN]: CAMPAIGN_TYPES.FEATURE_ANNOUNCEMENT,
  [FILTER_OPTIONS.FEATURES_ONLY]: CAMPAIGN_TYPES.FEATURE_ANNOUNCEMENT,
  // All other filters default to marketing
  [FILTER_OPTIONS.ALL_USERS]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.MARKETING_OPT_IN]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.ALL_MARKETING_OPT_OUTS]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.EXCLUDE_BY_DATE]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.EXCLUDE_BY_DATE_RANGE]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.EXCLUDE_TODAYS_RECIPIENTS]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.SELECT_NOT_EMAILED_TODAY]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.RECENT_USERS_30_DAYS]: CAMPAIGN_TYPES.MARKETING,
  [FILTER_OPTIONS.INACTIVE_USERS_90_DAYS]: CAMPAIGN_TYPES.MARKETING
}

// Email Preferences
export const EMAIL_PREFERENCES = {
  MARKETING_EMAILS: 'marketingEmails',
  FEATURE_ANNOUNCEMENTS: 'featureAnnouncements',
  WORKSPACE_NOTIFICATIONS: 'workspaceNotifications'
}

// Email Status
export const EMAIL_STATUS = {
  SENT: 'sent',
  FAILED: 'failed',
  SKIPPED: 'skipped',
  PENDING: 'pending'
}

// Skip Reasons
export const SKIP_REASONS = {
  OPTED_OUT_MARKETING: 'opted_out_marketing',
  OPTED_OUT_FEATURE_ANNOUNCEMENTS: 'opted_out_feature_announcements',
  OPTED_OUT: 'opted_out', // Legacy
  ALREADY_SENT_TODAY: 'already_sent_today',
  INVALID_EMAIL: 'invalid_email'
}

// Campaign Status
export const CAMPAIGN_STATUS = {
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
  PAUSED: 'paused'
}

/**
 * Get campaign type for a given filter
 * @param {string} filter - The filter name
 * @returns {string} - The campaign type (defaults to marketing)
 */
export function getCampaignTypeForFilter(filter) {
  return FILTER_TO_CAMPAIGN_TYPE[filter] || CAMPAIGN_TYPES.MARKETING
}

/**
 * Get email preference field to check based on campaign type
 * @param {string} campaignType - The campaign type
 * @returns {string} - The preference field name
 */
export function getPreferenceFieldForCampaignType(campaignType) {
  if (campaignType === CAMPAIGN_TYPES.FEATURE_ANNOUNCEMENT) {
    return EMAIL_PREFERENCES.FEATURE_ANNOUNCEMENTS
  }
  return EMAIL_PREFERENCES.MARKETING_EMAILS
}

/**
 * Get skip reason based on campaign type
 * @param {string} campaignType - The campaign type
 * @returns {string} - The skip reason
 */
export function getSkipReasonForCampaignType(campaignType) {
  if (campaignType === CAMPAIGN_TYPES.FEATURE_ANNOUNCEMENT) {
    return SKIP_REASONS.OPTED_OUT_FEATURE_ANNOUNCEMENTS
  }
  return SKIP_REASONS.OPTED_OUT_MARKETING
}
